declare global {
  interface Window {
    bootstrap: {
      Modal: new (el: Element) => any;
    };
  }
}

import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { HiClock, HiUser, HiEye, HiDocumentText } from "react-icons/hi";
import "./OrderHistory.scss";

interface OrderHistoryProps {
  formData: any;
  selectedLog: any;
  setSelectedLog: (log: any) => void;
}

export const OrderHistory: React.FC<OrderHistoryProps> = ({
  formData,
  selectedLog,
  setSelectedLog,
}) => {
  const { t } = useTranslation('orders');
  const { isRTL } = useLanguage();
  const [expandedLogId, setExpandedLogId] = React.useState<number | null>(null);

  const handleToggleLog = (logId: number, log: any) => {
    if (expandedLogId === logId) {
      setExpandedLogId(null);
    } else {
      setExpandedLogId(logId);
    }
  };

  const getFieldChanges = (log: any) => {
    try {
      const prev = JSON.parse(log.previousState || '{}');
      const current = JSON.parse(log.currentState || '{}');
      const changes: any[] = [];

      // Get all unique keys from both objects
      const allKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);

      allKeys.forEach(key => {
        const prevValue = prev[key];
        const currentValue = current[key];

        // Skip if values are the same
        if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
          changes.push({
            field: key,
            previous: prevValue,
            current: currentValue,
            isNew: !(key in prev),
            isDeleted: !(key in current)
          });
        }
      });

      return changes;
    } catch (error) {
      console.error('Error parsing log data:', error);
      return [];
    }
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="null-value">null</span>;
    }
    if (typeof value === 'boolean') {
      return <span className={`boolean-value ${value ? 'true' : 'false'}`}>{value.toString()}</span>;
    }
    if (typeof value === 'number') {
      return <span className="number-value">{value}</span>;
    }
    if (typeof value === 'string') {
      return <span className="string-value">{value}</span>;
    }
    if (typeof value === 'object') {
      return <span className="object-value">{JSON.stringify(value)}</span>;
    }
    return <span className="default-value">{String(value)}</span>;
  };

  const getFieldDisplayName = (fieldName: string) => {
    // Convert camelCase to readable format
    const readable = fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();

    // Common field mappings
    const fieldMappings: { [key: string]: string } = {
      'orderId': 'Order ID',
      'orderStatus': 'Order Status',
      'totalPrice': 'Total Price',
      'shippingFee': 'Shipping Fee',
      'customerId': 'Customer ID',
      'paymentMethodId': 'Payment Method',
      'currencyId': 'Currency',
      'countryId': 'Country',
      'cityId': 'City',
      'createdAt': 'Created Date',
      'updatedAt': 'Updated Date'
    };

    return fieldMappings[fieldName] || readable;
  };

  return (
    <div className={`order-history-container ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header Section */}
      <div className="history-header">
        <div className="section-title">
          <HiClock className="title-icon" />
          <h5>{t('table.orderHistory', 'Order History')}</h5>
        </div>
      </div>

      {/* Modern Table Card */}
      <div className="history-table-card">
        <div className="table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th className="th-user">
                  <HiUser className="th-icon" />
                  {t('table.user')}
                </th>
                <th className="th-date">
                  <HiClock className="th-icon" />
                  {t('table.actionDate')}
                </th>
                <th className="th-log">
                  <HiDocumentText className="th-icon" />
                  {t('table.log')}
                </th>
                <th className="th-actions">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {formData.orderLogs.length > 0 ? (
                formData.orderLogs.map((log: any) => (
                  <React.Fragment key={log.id}>
                    <tr className="table-row">
                      <td className="td-user">
                        <div className="user-info">
                          <div className="user-avatar">
                            <HiUser />
                          </div>
                          <span className="user-name">{log.user?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="td-date">
                        <div className="date-info">
                          <span className="date-text">
                            {log.actionDate
                              ? new Date(log.actionDate).toLocaleString("en-CA", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td className="td-log">
                        <div className="log-summary">
                          <span className="log-text">{log.actionSummary}</span>
                        </div>
                      </td>
                      <td className="td-actions">
                        <div className="action-buttons">
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleToggleLog(log.id, log)}
                            title={t('actions.view')}
                          >
                            <HiEye />
                            <span>{expandedLogId === log.id ? 'Hide' : 'View'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Beautiful Expandable Row */}
                    {expandedLogId === log.id && (
                      <tr className="expanded-row">
                        <td colSpan={4} className="expanded-content">
                          <div className="log-details-container">
                            <div className="details-header">
                              <HiDocumentText className="details-icon" />
                              <h4>Change Details</h4>
                            </div>

                            <div className="changes-list">
                              {getFieldChanges(log).length > 0 ? (
                                getFieldChanges(log).map((change, index) => (
                                  <div key={index} className="change-item">
                                    <div className="field-header">
                                      <span className="field-name">{getFieldDisplayName(change.field)}</span>
                                      {change.isNew && <span className="change-type new">New</span>}
                                      {change.isDeleted && <span className="change-type deleted">Deleted</span>}
                                      {!change.isNew && !change.isDeleted && <span className="change-type modified">Modified</span>}
                                    </div>

                                    <div className="value-comparison">
                                      <div className="value-section previous">
                                        <div className="value-label">Before</div>
                                        <div className="value-content">
                                          {change.isNew ? (
                                            <span className="empty-value">—</span>
                                          ) : (
                                            formatValue(change.previous)
                                          )}
                                        </div>
                                      </div>

                                      <div className="arrow-divider">
                                        <div className="arrow">→</div>
                                      </div>

                                      <div className="value-section current">
                                        <div className="value-label">After</div>
                                        <div className="value-content">
                                          {change.isDeleted ? (
                                            <span className="empty-value">—</span>
                                          ) : (
                                            formatValue(change.current)
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="no-changes">
                                  <HiDocumentText className="no-changes-icon" />
                                  <span>No field changes detected</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="empty-state">
                    <div className="empty-content">
                      <HiDocumentText className="empty-icon" />
                      <span className="empty-text">{t('messages.noLogs', 'No Logs found.')}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
};